import { type FormEvent, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { apiClient, ApiClientError } from "../lib/api-client";
import { usePublicConfig } from "../hooks/usePublicConfig";
import { useAuthPrincipal } from "../hooks/useAuthPrincipal";
import { TurnstileWidget } from "../components/TurnstileWidget";
import { describeError } from "../components/States";
import { usePageMeta } from "../lib/seo";

export function ContactPage() {
  usePageMeta({
    title: "Contact us",
    description: "Get in touch with the MarketOnBase team.",
    path: "/contact",
  });

  const { data: publicConfig } = usePublicConfig();
  const { isSignedIn, email: signedInEmail } = useAuthPrincipal();

  const [name, setName] = useState("");
  const [email, setEmail] = useState(signedInEmail ?? "");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");

  const mutation = useMutation({
    mutationFn: () => apiClient.sendContactMessage({ name, email, subject, message }),
    onSuccess: () => {
      setName("");
      setEmail(signedInEmail ?? "");
      setSubject("");
      setMessage("");
    },
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    mutation.mutate();
  }

  const humanCheckError =
    mutation.error instanceof ApiClientError &&
    mutation.error.code === "HUMAN_VERIFICATION_REQUIRED"
      ? "The human verification check didn't complete — please wait a moment and try again."
      : null;

  return (
    <div className="stack" style={{ maxWidth: 560 }}>
      <div>
        <h1>Contact us</h1>
        <p className="muted">
          Questions, feedback, or something not covered by the{" "}
          <Link to="/faq">FAQ</Link>? Send a message and we&apos;ll get back to you at
          the email you provide.
        </p>
      </div>

      {/* Mounted unconditionally, same reasoning as SignInPage: give it the whole time on this
          page to solve/auto-refresh in the background. Renders nothing when Turnstile isn't
          configured server-side. */}
      {publicConfig?.turnstileSiteKey && <TurnstileWidget siteKey={publicConfig.turnstileSiteKey} />}

      <form className="card stack" onSubmit={handleSubmit}>
        <div className="field">
          <label htmlFor="contact-name">Your name</label>
          <input
            id="contact-name"
            type="text"
            autoComplete="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            maxLength={100}
          />
        </div>

        <div className="field">
          <label htmlFor="contact-email">Your email</label>
          <input
            id="contact-email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            maxLength={200}
            readOnly={isSignedIn}
          />
          {isSignedIn && (
            <p className="text-sm muted">Using the email from your MarketOnBase sign-in.</p>
          )}
        </div>

        <div className="field">
          <label htmlFor="contact-subject">Subject</label>
          <input
            id="contact-subject"
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            required
            maxLength={200}
          />
        </div>

        <div className="field">
          <label htmlFor="contact-message">Message</label>
          <textarea
            id="contact-message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            required
            maxLength={5_000}
            rows={6}
          />
        </div>

        {humanCheckError && (
          <p className="field-error-text" role="alert">
            {humanCheckError}
          </p>
        )}
        {mutation.isError && !humanCheckError && (
          <p className="field-error-text" role="alert">
            {describeError(mutation.error)}
          </p>
        )}
        {mutation.isSuccess && (
          <p className="alert alert--success" role="status">
            Message sent — thanks! We&apos;ll get back to you soon.
          </p>
        )}

        <button
          type="submit"
          className="btn btn-primary"
          disabled={mutation.isPending || !name.trim() || !email.trim() || !subject.trim() || !message.trim()}
        >
          {mutation.isPending ? "Sending…" : "Send message"}
        </button>
      </form>
    </div>
  );
}
